(() => {
  const SELECTORS = {
    main: "[data-educational-main]",
    controller: "[data-educational-controller]",
    thumbs: "[data-educational-thumbs]",
    prev: "[data-educational-prev]",
    next: "[data-educational-next]",
    thumbButton: "[data-thumb-button]",
  };

  const prefersReducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

  const getSwiperCtor = () => (window.Swiper ? window.Swiper : null);

  class EducationalSlider extends HTMLElement {
    #main = null;
    #controller = null;
    #thumbs = null;

    #mql = null;
    #isDesktop = null;

    #thumbsEl = null;
    #onMqlChange = null;
    #onThumbButtonClick = null;

    connectedCallback() {
      const Swiper = getSwiperCtor();
      if (!Swiper) return console.warn("[EducationalSlider] Swiper not found on window.");

      const mainEl = this.querySelector(SELECTORS.main);
      const controllerEl = this.querySelector(SELECTORS.controller);
      const thumbsEl = this.querySelector(SELECTORS.thumbs);
      if (!mainEl || !controllerEl || !thumbsEl) {
        console.warn("[EducationalSlider] Missing required slider elements.", {
          mainEl,
          controllerEl,
          thumbsEl,
        });
        return;
      }
      this.#thumbsEl = thumbsEl;

      const isSingle = this.hasAttribute("data-single-slide");
      const prevEl = this.querySelector(SELECTORS.prev);
      const nextEl = this.querySelector(SELECTORS.next);

      this.#mql = window.matchMedia("(min-width: 768px)");
      this.#isDesktop = this.#mql.matches;

      this.#thumbs = this.#createThumbsSwiper(this.#isDesktop);

      const fadeSpeed = prefersReducedMotion() ? 0 : this.#isDesktop ? 330 : 220;

      // --- Main (fade) ---
      this.#main = new Swiper(mainEl, {
        loop: true,
        effect: "fade",
        slidesPerView: 1,
        fadeEffect: { crossFade: true },
        speed: fadeSpeed,
        centeredSlides: true,
        allowTouchMove: false,
        navigation: { prevEl, nextEl, disabledClass: "is-disabled" },
        a11y: { enabled: true },
        keyboard: { enabled: true, onlyInViewport: true },
      });

      // --- Controller (fade) ---
      this.#controller = new Swiper(controllerEl, {
        loop: true,
        effect: "fade",
        slidesPerView: 1,
        fadeEffect: { crossFade: true },
        speed: fadeSpeed,
        allowTouchMove: false,
        a11y: { enabled: true },
        autoHeight: true,
      });

      this.#bindThumbsUserNavigation();
      this.#bindThumbButtonClicks();

      this.#main.on("realIndexChange", (swiper) => {
        const i = swiper.realIndex;

        this.#syncThumbButtons(i);

        if (this.#controller && this.#controller.realIndex !== i) {
          this.#controller.slideToLoop(i);
        }

        const thumbSwiperIdx = this.#thumbSwiperIndexForMain(i);
        if (this.#thumbs && thumbSwiperIdx >= 0 && this.#thumbs.activeIndex !== thumbSwiperIdx) {
          this.#thumbs.slideTo(thumbSwiperIdx);
        }
      });

      if (isSingle) {
        prevEl?.setAttribute("disabled", "disabled");
        nextEl?.setAttribute("disabled", "disabled");
      }

      this.#onMqlChange = (e) => {
        const nextIsDesktop = e.matches;
        if (nextIsDesktop === this.#isDesktop) return;

        this.#isDesktop = nextIsDesktop;

        const i = this.#main?.realIndex ?? 0;

        this.#rebuildThumbs(nextIsDesktop, i);
      };

      if (this.#mql.addEventListener) this.#mql.addEventListener("change", this.#onMqlChange);
      else this.#mql.addListener(this.#onMqlChange);

      const i = this.#main.realIndex;
      this.#syncThumbButtons(i);
      if (this.#controller.realIndex !== i) this.#controller.slideToLoop(i, 0);
      const thumbSwiperIdx = this.#thumbSwiperIndexForMain(i);
      if (thumbSwiperIdx >= 0 && this.#thumbs.activeIndex !== thumbSwiperIdx) {
        this.#thumbs.slideTo(thumbSwiperIdx, 0);
      }
    }

    disconnectedCallback() {
      if (this.#onThumbButtonClick) {
        this.removeEventListener("click", this.#onThumbButtonClick);
        this.#onThumbButtonClick = null;
      }

      if (this.#mql && this.#onMqlChange) {
        if (this.#mql.removeEventListener)
          this.#mql.removeEventListener("change", this.#onMqlChange);
        else this.#mql.removeListener(this.#onMqlChange);
      }

      this.#main?.destroy?.(true, true);
      this.#controller?.destroy?.(true, true);
      this.#thumbs?.destroy?.(true, true);

      this.#main = null;
      this.#controller = null;
      this.#thumbs = null;

      this.#mql = null;
      this.#isDesktop = null;

      this.#thumbsEl = null;
      this.#onMqlChange = null;
    }

    #bindThumbsUserNavigation() {
      this.#thumbs.on("slideChange", (swiper) => {
        const slideEl = swiper.slides[swiper.activeIndex];
        const i = this.#mainIndexFromThumbSlideEl(slideEl) ?? swiper.activeIndex;
        this.#syncThumbButtons(i);
        if (this.#main?.realIndex !== i) {
          this.#main.slideToLoop(i);
        }
      });
    }

    #bindThumbButtonClicks() {
      this.#onThumbButtonClick = (e) => {
        const btn = e.target.closest?.(SELECTORS.thumbButton);
        if (!btn || !this.#thumbsEl?.contains(btn) || !this.#main) return;

        const mainIdx = this.#mainIndexFromThumbSlideEl(btn);
        if (mainIdx == null || !Number.isFinite(mainIdx)) return;

        if (this.#main.realIndex !== mainIdx) {
          this.#main.slideToLoop(mainIdx);
          return;
        }

        const thumbSwiperIdx = this.#thumbSwiperIndexForMain(mainIdx);
        if (thumbSwiperIdx >= 0 && this.#thumbs?.activeIndex !== thumbSwiperIdx) {
          this.#thumbs.slideTo(thumbSwiperIdx);
        }
      };

      this.addEventListener("click", this.#onThumbButtonClick);
    }

    #mainIndexFromThumbSlideEl(el) {
      if (!el?.dataset) return null;
      const raw = el.dataset.mainSlideIndex;
      if (raw == null || raw === "") return null;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    }

    #thumbSwiperIndexForMain(mainIndex) {
      const buttons = [...this.querySelectorAll(SELECTORS.thumbButton)];
      return buttons.findIndex((b) => this.#mainIndexFromThumbSlideEl(b) === mainIndex);
    }

    #createThumbsSwiper(isDesktop) {
      const Swiper = getSwiperCtor();
      const speed = prefersReducedMotion() ? 0 : isDesktop ? 330 : 220;

      return new Swiper(this.#thumbsEl, {
        loop: false,
        speed,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        resistanceRatio: 0.65,
        threshold: 6,
        a11y: { enabled: true },
        keyboard: { enabled: true, onlyInViewport: true },

        centeredSlides: isDesktop ? false : false,
        slidesPerView: isDesktop ? "auto" : 1,
        spaceBetween: isDesktop ? 68 : 10,

        effect: isDesktop || prefersReducedMotion() ? "slide" : "fade",
        fadeEffect: { crossFade: true },

        on: {
          afterInit: () => {
            this.#updateThumbsOverflowState();
          },
          resize: () => this.#updateThumbsOverflowState(),
          update: () => this.#updateThumbsOverflowState(),
        },
      });
    }

    #rebuildThumbs(nextIsDesktop, realIndex) {
      this.#thumbs?.destroy?.(true, true);

      this.#thumbs = this.#createThumbsSwiper(nextIsDesktop);
      this.#bindThumbsUserNavigation();

      this.#syncThumbButtons(realIndex);
      const thumbSwiperIdx = this.#thumbSwiperIndexForMain(realIndex);
      if (thumbSwiperIdx >= 0) this.#thumbs.slideTo(thumbSwiperIdx);
    }

    #syncThumbButtons(activeIndex) {
      const buttons = this.querySelectorAll(SELECTORS.thumbButton);
      buttons.forEach((btn, i) => {
        const slideIdx = this.#mainIndexFromThumbSlideEl(btn) ?? i;
        const isActive = slideIdx === activeIndex;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-current", isActive ? "true" : "false");
        btn.setAttribute("aria-selected", isActive ? "true" : "false");
      });
    }

    #updateThumbsOverflowState() {
      if (!this.#thumbsEl) return;

      const wrapper = this.#thumbsEl.querySelector(".swiper-wrapper");
      if (!wrapper) return;

      const containerWidth = Math.ceil(this.#thumbsEl.clientWidth);
      const wrapperWidth = Math.ceil(wrapper.scrollWidth);
      const hasOverflow = wrapperWidth > containerWidth;

      this.#thumbsEl.classList.toggle("no-overflow", !hasOverflow);
    }
  }

  if (!customElements.get("educational-slider")) {
    customElements.define("educational-slider", EducationalSlider);
  }
})();
